import { ApprovalView } from '../../../../../services/RefundService/types/refundTypes';

// Dialog configuration
export const DIALOG_CONFIG = {
  maxWidth: 'sm' as const,
  fullWidth: true,
} as const;

// Status configuration
export const STATUS_CONFIG = {
  Approved: { color: 'success' as const, label: 'Approved' },
  Rejected: { color: 'error' as const, label: 'Rejected' },
  Pending: { color: 'warning' as const, label: 'Pending' },
} as const;

// Action types
export const ACTION_TYPES = {
  APPROVE: 'approve' as const,
  REJECT: 'reject' as const,
} as const;

export type ApprovalAction = (typeof ACTION_TYPES)[keyof typeof ACTION_TYPES];
export type ApprovalStatus = keyof typeof STATUS_CONFIG;

// Helper functions
export const getInitialAction = (approval: ApprovalView): ApprovalAction => {
  if (approval.isApproved) return ACTION_TYPES.APPROVE;
  if (approval.isRejected) return ACTION_TYPES.REJECT;
  return ACTION_TYPES.APPROVE;
};

export const getCurrentStatus = (approval: ApprovalView): ApprovalStatus => {
  if (approval.isApproved) return 'Approved';
  if (approval.isRejected) return 'Rejected';
  return 'Pending';
};

export const getStatusColor = (status: ApprovalStatus) => STATUS_CONFIG[status].color;

export const formatCurrency = (amount: number): string => {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

export const getWarningMessage = (approval: ApprovalView): string => {
  const hasExistingDecision = approval.approvedDate || approval.rejectedDate;
  const currentAction = getInitialAction(approval);

  if (!hasExistingDecision) {
    return 'Please review the refund details carefully before making your decision.';
  }

  if (currentAction === ACTION_TYPES.APPROVE && approval.approvedDate) {
    return 'This refund has already been approved. You can change your decision if needed.';
  }

  if (currentAction === ACTION_TYPES.REJECT && approval.rejectedDate) {
    return 'This refund has already been rejected. You can change your decision if needed.';
  }

  return 'You can change your previous decision. Please review the refund details carefully.';
};

export const createUpdatedApproval = (
  approval: ApprovalView,
  action: ApprovalAction,
  comments: string,
  rejectionReason: string
): ApprovalView => ({
  ...approval,
  isApproved: action === ACTION_TYPES.APPROVE,
  isRejected: action === ACTION_TYPES.REJECT,
  approvedDate: action === ACTION_TYPES.APPROVE ? new Date().toISOString() : undefined,
  rejectedDate: action === ACTION_TYPES.REJECT ? new Date().toISOString() : undefined,
  comments: comments.trim() || undefined,
  rejectionReason: action === ACTION_TYPES.REJECT ? rejectionReason.trim() : undefined,
});
